import React, { useState } from "react";
import { Typography, Box, Grid2, ListItemText, useTheme, Skeleton, ListItem, ListItemIcon, List } from "@mui/material";
import styles from "../../../styles/ProjectList.module.css";
import Highcharts from "highcharts/highstock";
import PieChart from "highcharts-react-official";
import { RxSize } from "react-icons/rx";
import { useGetIssuesAgeingAnalysisQuery } from '../../../store/slices/apis/issuesDashboardSlice';
import IssuesPopup from "./IssuesPopup";
import { useParams } from "react-router-dom";
import { FaSquareFull } from "react-icons/fa6";


const AgingAnalysisGraph = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { data: IssuesAgeingAnalysis = [], isLoading } = useGetIssuesAgeingAnalysisQuery({
    projectId: projectId
  });
  const totalcount = IssuesAgeingAnalysis?.data?.reduce((sum, item) => sum + item.count, 0) || 0;

  const moduleColorMap = {
    '0-30 Days': '#3F766E',
    '31-60 Days': '#FDCA40',
    '61-90 Days': '#E72B33',
    '90+ Days': '#FF7E5C',
  };


  const distributionData = IssuesAgeingAnalysis?.data?.map((item, index) => ({
    y: item.count,
    value: item.count,
    color: moduleColorMap[item.range],
    name: item.range,
    percentage: (item.count / totalcount) * 100,
  })) || [];

  const options = {
    chart: {
      type: "pie",
      width: 200,
      height: 200,
      marginBottom: 30,
      marginTop: 0
    },
    title: {
      text: '',
    },
    subtitle: {
      useHTML: true,
      text: '',
      //text: '<div style="width: 28px; height: 28px; background-color: white; border-radius: 40%; padding-top: 5px; padding-left: 2px; -moz-box-shadow: 1px 1px 4px #f3f3f3;-webkit-box-shadow: 1px 1px 4px #f3f3f3;box-shadow: 0 0 4px #f3f3f3;">47%</div>',
      align: 'center',
      verticalAlign: 'middle',
      style: { "fontSize": "12px", "fontWeight": "500", "color": "#414D55", "lineHeight": "20px", "letterSpacing": "0.5px" },
      y: -65, x: 86,
    },
    credits: {
      enabled: false
    },
    tooltip: {
      formatter: function () {
        // Customize the hover text here
        return `<b style='color: ${this.color}'>${this.name}</b>: ${this.y}`;
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        borderWidth: 0,
        borderRadius: 0,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,
        innerSize: 80,
        //startAngle: -20,
        point: {
          events: {
            click: function () {
              setPopupData({
                name: this.name,
                value: this.y,
              });
              setOpen(true);
            }
          }
        },
      }
    },
    series: [
      {
        name: "",
        dataLabels: {
          verticalAlign: 'top',
          enabled: true,
          style: {
            fontWeight: 600,
            fontSize: '11px',
            color: '#FFFFFF',
            lineHeight: '40px',
            textOutline: 'none',
          },
          //style: { "fontSize": "11px", "fontWeight": "600"},
          //connectorWidth: 1,
          distance: -16,
          //connectorColor: '#000000',
          formatter: function () {
            return Math.round(this.percentage) + '%';
          }
        },
        color: "#006600",
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 3,
          states: {
            hover: {
              enabled: true,
              lineWidth: 1
            }
          }
        },
        data: distributionData,
      }
    ]
  };

  const getDateRangeFromDaysRange = (range) => {

    const today = new Date();

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let dateRange = '';
    if (range === '0-30 Days') {
      const fromDate30 = new Date(today);
      fromDate30.setDate(today.getDate() - 30);
      const toDate30 = today;
      dateRange = formatDate(fromDate30) + ',' + formatDate(toDate30);
    }
    else if (range === '31-60 Days') {
      const fromDate60 = new Date(today);
      fromDate60.setDate(today.getDate() - 60);
      const toDate60 = new Date(today);
      toDate60.setDate(today.getDate() - 31);
      dateRange = formatDate(fromDate60) + ',' + formatDate(toDate60);
    }
    else if (range === '61-90 Days') {
      const fromDate90 = new Date(today);
      fromDate90.setDate(today.getDate() - 90);
      const toDate90 = new Date(today);
      toDate90.setDate(today.getDate() - 61);
      dateRange = formatDate(fromDate90) + ',' + formatDate(toDate90);
    }
    else if (range === '90+ Days') {
        const fromDateOld = new Date(today);
        fromDateOld.setDate(today.getDate() - 365); 
        const toDateOld = new Date(today);
        toDateOld.setDate(today.getDate() - 91);
        dateRange = formatDate(fromDateOld) + ',' + formatDate(toDateOld);
    }

    return { dateRange: dateRange, };
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
        <Box sx={{}}>
          <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
            Aging Analysis
          </Typography>
          <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
            Issue aging to ensure timely resolution and maintain project momentum.
          </Typography>
        </Box>

        <Grid2 container>
          <Grid2 size={{ xs: 12, md: 7 }}>
            {[...Array(3)].map((_, index) => (
              <Grid2 container direction="row" spacing={1} key={index} sx={{ marginLeft: "10px", paddingTop: index === 0 ? '40px' : '10px' }}>
                <Box sx={{ width: "25px", padding: "8px" }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box sx={{ width: "130px", padding: "8px" }}>
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box sx={{ width: "30px", padding: "4px" }}>
                  <Skeleton variant="text" width={20} height={28} />
                </Box>
              </Grid2>
            ))}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="circular" width={120} height={120} />
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
      <Box sx={{}}>
        <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
          Aging Analysis
        </Typography>
        <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
          Issue aging to ensure timely resolution and maintain project momentum.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", }}>
        {distributionData?.length === 0 ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No data available to display the chart.
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ padding: 0, mb: 4 }}>
              {distributionData?.map((val, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon sx={{ minWidth: '20px' }}>
                    <FaSquareFull
                      style={{ color: `${val?.color}`, height: '14px', width: '14px' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", letterSpacing: "-0.4px" }}>
                          {val?.name}:{' '}
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", color: "#182A4E" }}>
                          {val?.value}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ flexShrink: 0 }}>
              <PieChart highcharts={Highcharts} options={options} />
            </Box>
          </>
        )}

      </Box>
      {open && (() => {
        const { dateRange } = getDateRangeFromDaysRange(popupData.name);

        return (
          <IssuesPopup title={'Issues Ageing Analysis'} value={dateRange} status={'Ageing Analysis'} onOpen={open} onClose={() => setOpen(false)} />
        )
      })()}
    </Box>

  );
};

export default AgingAnalysisGraph;